// Rapprochement entre l'intitulé de l'export comptable et les clients MyALFA.
// Les codes et noms de la comptabilité ne sont pas identiques à ceux de
// MyALFA : on propose un client, jamais on ne l'affecte sans validation.
//
// Règles : accents, casse, ponctuation et formes juridiques (SARL, SCI, M,
// MME...) ignorés ; ordre des mots indifférent ("DUPONT Marie" = "Marie
// Dupont") ; petites fautes de frappe tolérées (1 lettre d'écart, 2 sur les
// mots longs) ; un client est comparé sous son nom ET sous nom + prénom du
// contact.

const FORMES = new Set([
  "SARL", "EURL", "SAS", "SASU", "SA", "SCI", "SCM", "SCP", "SELARL", "SNC", "EI", "EIRL",
  "MR", "MME", "MLLE", "MONSIEUR", "MADAME", "M", "STE", "SOCIETE", "ET", "DE", "DU", "DES", "LA", "LE", "LES", "L", "D",
]);

const normaliser = (s) =>
  String(s || "")
    .normalize("NFD")
    .replace(/[̀-ͯ]/g, "")
    .toUpperCase()
    .replace(/[^A-Z0-9]+/g, " ")
    .trim();

const jetons = (s) => normaliser(s).split(" ").filter((t) => t && !FORMES.has(t));

// Distance d'édition, bornée : au-delà de max on abandonne tôt.
function distance(a, b, max) {
  if (a === b) return 0;
  if (Math.abs(a.length - b.length) > max) return max + 1;
  let prev = Array.from({ length: b.length + 1 }, (_, i) => i);
  for (let i = 1; i <= a.length; i++) {
    const cur = [i];
    let mini = i;
    for (let j = 1; j <= b.length; j++) {
      const v = Math.min(prev[j] + 1, cur[j - 1] + 1, prev[j - 1] + (a[i - 1] === b[j - 1] ? 0 : 1));
      cur.push(v);
      if (v < mini) mini = v;
    }
    if (mini > max) return max + 1;
    prev = cur;
  }
  return prev[b.length];
}

// 0 = identiques, 1 = faute tolérée, null = différents.
function ecart(a, b) {
  if (a === b) return 0;
  const n = Math.min(a.length, b.length);
  if (n < 4) return null;
  const tolere = n >= 9 ? 2 : 1;
  return distance(a, b, tolere) <= tolere ? 1 : null;
}

// Apparie chaque mot du plus court avec un mot distinct du plus long.
function comparer(a, b) {
  const [court, long] = a.length <= b.length ? [a, b] : [b, a];
  const pris = new Set();
  let apparies = 0;
  let fautes = 0;
  for (const t of court) {
    let meilleur = -1;
    let meilleurEcart = 9;
    long.forEach((u, i) => {
      if (pris.has(i)) return;
      const e = ecart(t, u);
      if (e !== null && e < meilleurEcart) {
        meilleur = i;
        meilleurEcart = e;
      }
    });
    if (meilleur >= 0) {
      pris.add(meilleur);
      apparies += 1;
      fautes += meilleurEcart;
    }
  }
  return { court: court.length, long: long.length, apparies, fautes };
}

// Index : chaque client avec ses variantes de nom.
export function indexerClients(clients) {
  return (clients || [])
    .filter((c) => c && c.id != null)
    .map((c) => {
      const nom = jetons(c.nom);
      const contact = jetons(`${c.nomClient || ""} ${c.prenomClient || ""}`);
      const variantes = [nom, contact].filter((j) => j.length);
      return { c, variantes, nomPremier: nom.length > 0 };
    })
    .filter((e) => e.variantes.length);
}

// { client, force } : "exacte" (mêmes mots, dans n'importe quel ordre, sans
// faute) ou "probable" (faute de frappe, ou nom partiel). Plusieurs candidats
// équivalents : aucune proposition (ambigu, affectation manuelle).
export function proposerClient(intitule, index) {
  const mots = jetons(intitule);
  if (!mots.length) return null;

  const notes = [];
  index.forEach((e) => {
    let meilleure = null;
    e.variantes.forEach((v, i) => {
      const r = comparer(mots, v);
      // Tous les mots du plus court retrouvés ; au moins deux mots, ou un mot
      // long, pour éviter qu'un prénom courant ne rapproche des inconnus ; et
      // au moins la moitié du plus long (sinon "MARTIN" attrape tout).
      if (r.apparies !== r.court) return;
      if (r.court < 2 && v.concat(mots).every((t) => t.length < 5)) return;
      if (r.apparies / r.long < 0.5) return;
      const identique = r.court === r.long && r.fautes === 0;
      // Le nom de la fiche prime sur le nom du contact : "ALADZIC Daniela" est
      // d'abord la fiche "Aladzic Daniela", pas une société dirigée par elle.
      const sourceNom = e.nomPremier && i === 0 ? 2000 : 0;
      const score = sourceNom + (identique ? 1000 : 0) + (r.apparies / r.long) * 100 - r.fautes * 5 + r.apparies;
      if (!meilleure || score > meilleure.score) meilleure = { score, identique };
    });
    if (meilleure) notes.push({ c: e.c, ...meilleure });
  });
  if (!notes.length) return null;

  notes.sort((x, y) => y.score - x.score);
  if (notes.length > 1 && Math.abs(notes[0].score - notes[1].score) < 0.001) return null;
  return { client: notes[0].c, force: notes[0].identique ? "exacte" : "probable" };
}
